// src/services/polls.js
//
// 🟢 POLLS (web) — create, read and vote, interoperable with the phone.
//
// The shape is unusual, so the rules that actually matter:
//
//   • The QUESTION and the OPTION TEXTS are E2EE. The message doc stores only
//     `options: [{ id, votes: [] }]` — no text at all — and the readable half lives
//     inside `pollPerDeviceKeys`, sealing exactly
//         {"question": <string>, "optionTexts": {<optId>: <string>}, "multipleChoice": <bool>}
//     `optionTexts` KEY ORDER must match `options` order: the phone falls back to
//     position when an id doesn't match (services/pollText.ts:59).
//   • `multipleChoice` exists ONLY inside that sealed JSON. Put it on the doc instead
//     and the phone's applyPollText overwrites it from the payload, then treats the
//     poll as single-choice and silently clears other options on every vote.
//   • VOTES ARE PLAINTEXT — raw uids inside `options[].votes`. Three writes per vote:
//     the message doc, `poll_registry/{id}` (how forwarded copies stay in sync), and a
//     `vote_action` doc appended to the SAME messages collection.
//   • Sealing with no `senderDeviceId` produces a permanently unreadable poll, because
//     there is no plaintext copy to fall back to.

import {
  collection, doc, addDoc, setDoc, serverTimestamp, runTransaction, deleteField, increment,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage, getSocket } from './socket';
import { writeChatDoc } from './mediaUpload';
import { isGhostRow } from './ghostRows';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

/** Merge the sealed text back onto the doc's id-only options. */
export function readablePoll(m) {
  const p = m?._payload || null;
  const opts = Array.isArray(m?.options) ? m.options : [];
  if (!p) {
    // plaintext fallback polls (older clients) carry question/text on the doc itself
    return {
      question: m?.question || null,
      multipleChoice: m?.multipleChoice === true,
      options: opts.map((o) => ({ id: o.id, text: o.text || null, votes: Array.isArray(o.votes) ? o.votes : [] })),
      locked: !m?.question,
    };
  }
  const texts = p.optionTexts || {};
  const keys = Object.keys(texts);
  return {
    question: p.question || m?.question || '',
    multipleChoice: p.multipleChoice === true,
    options: opts.map((o, i) => ({
      id: o.id,
      // id first, then POSITION — same fallback the phone uses
      text: texts[o.id] ?? texts[keys[i]] ?? o.text ?? '',
      votes: Array.isArray(o.votes) ? o.votes : [],
    })),
    locked: false,
  };
}

/** The phone's `_applyDelta` (PollVotes.ts:129), reproduced exactly — including the
 *  single-choice rule that adding a vote clears the voter from every other option. */
function applyDelta(options, optionId, action, uid, multipleChoice) {
  const out = (options || []).map((o) => ({ id: o.id, votes: Array.isArray(o.votes) ? [...o.votes] : [] }));
  for (const o of out) {
    if (o.id === optionId) {
      if (action === 'add') { if (!o.votes.includes(uid)) o.votes.push(uid); }
      else o.votes = o.votes.filter((u) => u !== uid);
    } else if (action === 'add' && multipleChoice !== true) {
      o.votes = o.votes.filter((u) => u !== uid);
    }
  }
  return out;
}

const tallyOf = (opts) => {
  const totalVotes = opts.reduce((n, o) => n + (o.votes?.length || 0), 0);   // flat: multi counts twice
  const votedUsers = Array.from(new Set(opts.flatMap((o) => o.votes || []))); // unique voters
  return { totalVotes, votedUsers };
};

// ── create ────────────────────────────────────────────────────────────────
export async function createPoll({
  chat, myUid, myName, question, options, multipleChoice = false, privateVoting = false, reply,
  mentions, mentionsEveryone,
}) {
  /* 🟥 A SEALED QUESTION CANNOT BE READ BY ANYTHING DOWNSTREAM. A caption travels
     in the clear, so the relay can work out who it named on its way past; a poll's
     question and options are inside pollPerDeviceKeys, so this is the last place the
     plaintext exists and therefore the only place the question can be asked. */
  const _named = Array.isArray(mentions) ? mentions : [];
  const _namedAll = !!mentionsEveryone;
  const q = (question || '').trim();
  const texts = (options || []).map((o) => (o || '').trim()).filter(Boolean);
  if (!q || texts.length < 2) throw new Error('A poll needs a question and at least two options.');

  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = `temp-poll-${Date.now()}`;

  // ids in the phone's format, and the two structures kept index-aligned
  const stamp = Date.now();
  const ids = texts.map((_, i) => `opt_${stamp}_${i}`);
  const optionTexts = {};
  ids.forEach((id, i) => { optionTexts[id] = texts[i]; });
  const optionsDB = ids.map((id) => ({ id, votes: [] }));

  const sealed = await sealForUsers(
    JSON.stringify({ question: q, optionTexts, multipleChoice: !!multipleChoice }),
    participants,
  );
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
    throw new Error("Couldn't secure the poll — it was not sent.");
  }

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;

  await setMessageDoc(msgRef, {
    type: 'poll',
    ...(_named.length ? { mentions: _named } : {}),
    ...(_namedAll ? { mentionsEveryone: true } : {}),
    clientTempId,
    pollPerDeviceKeys: sealed.perDeviceKeys,
    senderDeviceId: sealed.senderDeviceId,
    options: optionsDB,          // ids + empty votes ONLY — the text is sealed
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    totalVotes: 0,
    votedUsers: [],
    expiresAt: null,
    privateVoting: isGroup ? !!privateVoting : false,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    // the sealed quote — an object in Firestore, an object on the wire (the server
    // re-stringifies replyTo itself); the two *PerDeviceKeys maps must stay objects
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: `📊 ${q}`, clientMsgId: clientTempId,
    mentions: _named, mentionsEveryone: _namedAll,
    extra: {
      type: 'poll',
      clientTempId,
      pollPerDeviceKeys: sealed.perDeviceKeys,
      senderDeviceId: sealed.senderDeviceId,
      options: JSON.stringify(optionsDB),      // a STRING over the socket, like the phone
      multipleChoice: String(!!multipleChoice),
      privateVoting: String(isGroup ? !!privateVoting : false),
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview: `📊 ${q}` });
  return messageId;
}

// ── vote ──────────────────────────────────────────────────────────────────
/**
 * @param a.registryOptional  when true, a vote still lands on the MESSAGE even if
 *   the shared registry write is refused. Off by default so the app's behaviour
 *   is untouched; the guest page turns it on — see the note below.
 */
export async function castVote({ chat, myUid, myName, message, optionId, optionText, registryOptional = false }) {
  const chatId = chat.id;
  const msgId = String(message.id);
  const mine = (message.options || []).find((o) => o.id === optionId);
  const already = Array.isArray(mine?.votes) && mine.votes.includes(myUid);
  const action = already ? 'remove' : 'add';
  const multipleChoice = !!(message._payload?.multipleChoice ?? message.multipleChoice);
  const regKey = String(message.originalPollId || msgId);

  const msgRef = doc(db, 'chats', chatId, 'messages', msgId);
  const regRef = doc(db, 'poll_registry', regKey);

  let merged = [];
  let tally = { totalVotes: 0, votedUsers: [] };

  /* 🟥 THE REGISTRY AND THE MESSAGE ARE IN ONE TRANSACTION, WHICH MEANS A
     REFUSAL ON EITHER LOSES BOTH. `poll_registry` is one of the collections the
     repo's rules file does not cover (see GUEST_RULES_SNIPPET.rules), so whether
     an anonymous guest may write it depends on rules nobody here can see. If it
     is closed to them, the transaction aborts and the vote does not record at
     all — the option they tapped simply springs back, with no error a person
     could act on.

     A guest therefore votes with `registryOptional`. The full write is still
     attempted first, because keeping forwarded copies in step is worth having;
     only a PERMISSION failure falls back to updating the message alone. Any
     other error still throws — an offline or contended write must not be
     quietly downgraded into a half-write. */
  const runVote = async (withRegistry) => runTransaction(db, async (tx) => {
    const snap = await tx.get(msgRef);
    const cur = snap.exists() ? snap.data() : null;
    const base = Array.isArray(cur?.options) && cur.options.length ? cur.options : (message.options || []);
    merged = applyDelta(base, optionId, action, myUid, multipleChoice);
    tally = tallyOf(merged);
    const payload = { options: merged.map((o) => ({ id: o.id, votes: o.votes || [] })), ...tally };
    if (snap.exists()) tx.update(msgRef, payload);
    // the registry is what keeps FORWARDED copies of this poll in step
    if (withRegistry) tx.set(regRef, { ...payload, lastUpdated: serverTimestamp() }, { merge: true });
  });

  try {
    await runVote(true);
  } catch (e) {
    const denied = e?.code === 'permission-denied' || /permission/i.test(e?.message || '');
    if (!registryOptional || !denied) throw e;
    console.warn('[poll] registry write refused — recording the vote on the message only');
    await runVote(false);
  }

  // the phone appends this alongside the messages; it is how other devices learn of a
  // vote without re-reading the whole poll. (Renderers must skip type:'vote_action'.)
  await addDoc(collection(db, 'chats', chatId, 'messages'), {
    type: 'vote_action',
    targetMessageId: msgId,
    targetTempId: message.clientTempId || null,
    optionId,
    action,
    senderId: myUid,
    timestamp: serverTimestamp(),
    originalPollId: regKey,
    originalChatId: chatId,
  }).catch(() => {});

  const others = (chat.participants || []).filter((u) => u && u !== myUid);
  const sock = getSocket();
  try {
    others.forEach((uid) => {
      sock?.emit('new-message', {
        chatId, fromUserId: myUid, toUserId: uid, type: 'vote_action',
        messageId: msgId, targetTempId: message.clientTempId || null,
        optionId, action, originalPollId: regKey, originalChatId: chatId,
      });
    });
  } catch {}

  // 🟢 the chat row's "Abdullah voted: Yes" line — the phone drives it from
  //    lastMessageSystem, and only ever on an ADD (ChatModal ~11300).
  if (action === 'add') {
    const name = myName || 'You';
    const label = optionText || 'an option';
    const counts = {};
    others.forEach((uid) => { counts[uid] = increment(1); });
    await setDoc(doc(db, 'chats', chatId), {
      lastMessage: `${name} voted: ${label}`,
      lastMessageSystem: { type: 'vote', name, targetText: label },
      timestamp: serverTimestamp(),
      lastMessageSenderId: myUid,
      ...(Object.keys(counts).length ? { unreadCounts: { ...counts, [myUid]: 0 } } : {}),
    }, { merge: true }).catch(() => {});
  }

  return { action, options: merged, ...tally };
}

/** `vote_action` docs live in the messages collection but are NOT messages. */
export const isPollSideEffect = (m) => isGhostRow(m);

export { deleteField };
